import { create } from 'zustand';

/**
 * Voice & video preferences. Stored per device (in localStorage), not per
 * account, because device IDs and audio setups differ between machines.
 */

export type InputProfile = 'isolation' | 'studio' | 'custom';

export interface VoiceSettings {
  inputDeviceId: string;
  outputDeviceId: string;
  videoDeviceId: string;
  /** 0–200 (%). Applied as a gain stage before transmission. */
  inputVolume: number;
  /** 0–100 (%). */
  outputVolume: number;
  inputProfile: InputProfile;
  /** Custom-profile values (the other profiles override these; see `effectiveVoiceSettings`). */
  autoSensitivity: boolean;
  /** Manual voice-activity threshold in dBFS (-100 = always transmit, 0 = never). */
  sensitivityDb: number;
  noiseSuppression: boolean;
  echoCancellation: boolean;
  autoGainControl: boolean;
  pushToTalk: boolean;
  /** `KeyboardEvent.code` of the push-to-talk key. */
  pttKey: string | null;
  pttReleaseDelayMs: number;
  mirrorVideo: boolean;
  /** Per-user playback volume, 0–100 (%). Missing = 100. */
  userVolumes: Record<string, number>;
  /** Users muted locally (only for you). */
  localMutes: Record<string, boolean>;
  /** Soundboard sounds other people play: 0–100 (%), 0 = off. */
  soundboardVolume: number;
}

export const DEFAULT_VOICE_SETTINGS: VoiceSettings = {
  inputDeviceId: 'default',
  outputDeviceId: 'default',
  videoDeviceId: 'default',
  inputVolume: 100,
  outputVolume: 100,
  inputProfile: 'isolation',
  autoSensitivity: true,
  sensitivityDb: -50,
  noiseSuppression: true,
  echoCancellation: true,
  autoGainControl: true,
  pushToTalk: false,
  pttKey: null,
  pttReleaseDelayMs: 200,
  mirrorVideo: true,
  userVolumes: {},
  localMutes: {},
  soundboardVolume: 60,
};

export interface EffectiveProcessing {
  noiseSuppression: boolean;
  echoCancellation: boolean;
  autoGainControl: boolean;
  /** How transmission is gated. */
  gate: { mode: 'ptt' } | { mode: 'open' } | { mode: 'auto' } | { mode: 'manual'; thresholdDb: number };
}

/** Resolves the input profile into concrete processing settings. */
export function effectiveVoiceSettings(s: VoiceSettings): EffectiveProcessing {
  const base =
    s.inputProfile === 'isolation'
      ? { noiseSuppression: true, echoCancellation: true, autoGainControl: true, autoSensitivity: true }
      : s.inputProfile === 'studio'
        ? { noiseSuppression: false, echoCancellation: false, autoGainControl: false, autoSensitivity: false }
        : { noiseSuppression: s.noiseSuppression, echoCancellation: s.echoCancellation, autoGainControl: s.autoGainControl, autoSensitivity: s.autoSensitivity };

  let gate: EffectiveProcessing['gate'];
  if (s.pushToTalk && s.pttKey) gate = { mode: 'ptt' };
  else if (s.inputProfile === 'studio') gate = { mode: 'open' };
  else if (base.autoSensitivity) gate = { mode: 'auto' };
  else if (s.sensitivityDb <= -100) gate = { mode: 'open' };
  else gate = { mode: 'manual', thresholdDb: s.sensitivityDb };

  return { noiseSuppression: base.noiseSuppression, echoCancellation: base.echoCancellation, autoGainControl: base.autoGainControl, gate };
}

const STORAGE_KEY = 'clanfire:voice-settings';

function load(): VoiceSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_VOICE_SETTINGS, ...(JSON.parse(raw) as Partial<VoiceSettings>) } : DEFAULT_VOICE_SETTINGS;
  } catch {
    return DEFAULT_VOICE_SETTINGS;
  }
}

type ProcessingKey = 'noiseSuppression' | 'echoCancellation' | 'autoGainControl' | 'autoSensitivity';

interface VoiceSettingsState extends VoiceSettings {
  update: (patch: Partial<VoiceSettings>) => void;
  /**
   * Changes a processing option. Like other chat apps, tweaking one of these
   * while on a preset profile switches to Custom, keeping the preset's values.
   */
  setProcessing: (key: ProcessingKey, value: boolean) => void;
  setUserVolume: (userId: string, volume: number) => void;
  setLocalMute: (userId: string, muted: boolean) => void;
  reset: () => void;
}

export const useVoiceSettings = create<VoiceSettingsState>((set, get) => ({
  ...load(),
  update: (patch) => set(patch),
  setProcessing: (key, value) => {
    const s = get();
    if (s.inputProfile === 'custom') return set({ [key]: value });
    const eff = effectiveVoiceSettings(s);
    set({
      inputProfile: 'custom',
      noiseSuppression: eff.noiseSuppression,
      echoCancellation: eff.echoCancellation,
      autoGainControl: eff.autoGainControl,
      autoSensitivity: s.inputProfile === 'isolation',
      [key]: value,
    });
  },
  setUserVolume: (userId, volume) =>
    set((s) => {
      const userVolumes = { ...s.userVolumes };
      if (volume === 100) delete userVolumes[userId];
      else userVolumes[userId] = Math.max(0, Math.min(100, Math.round(volume)));
      return { userVolumes };
    }),
  setLocalMute: (userId, muted) =>
    set((s) => {
      const localMutes = { ...s.localMutes };
      if (muted) localMutes[userId] = true;
      else delete localMutes[userId];
      return { localMutes };
    }),
  // Per-user volumes are preferences about people, not devices: keep them on reset.
  reset: () => set((s) => ({ ...DEFAULT_VOICE_SETTINGS, userVolumes: s.userVolumes, localMutes: s.localMutes })),
}));

// Persist every change (the action functions are skipped by JSON.stringify).
useVoiceSettings.subscribe((state) => {
  try {
    const { update: _u, setProcessing: _s, reset: _r, setUserVolume: _v, setLocalMute: _m, ...data } = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* storage unavailable — settings still apply for this session */
  }
});

/** Human label for a bound key (a `KeyboardEvent.code`, or a `key` value), e.g. "KeyV" → "V", "Backquote" → "`". */
export function keyLabel(code: string | null): string {
  if (!code) return 'Not set';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  const named: Record<string, string> = {
    Backquote: '`',
    Minus: '-',
    Equal: '=',
    BracketLeft: '[',
    BracketRight: ']',
    Backslash: '\\',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
    Space: 'Space',
    CapsLock: 'Caps Lock',
    ShiftLeft: 'Left Shift',
    ShiftRight: 'Right Shift',
    ControlLeft: 'Left Ctrl',
    ControlRight: 'Right Ctrl',
    AltLeft: 'Left Alt',
    AltRight: 'Right Alt',
    MetaLeft: 'Left Meta',
    MetaRight: 'Right Meta',
  };
  if (named[code]) return named[code]!;
  return code.length === 1 ? code.toUpperCase() : code;
}
